import { useState, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';

// Seller-marketplace display of a product, set from the back-office "Marketplace vendeur" page
export interface MarketplaceDisplay {
    hidden?: boolean;
    countries?: string[];
    displayPrice?: number | null;
    // Lead volume per country card, e.g. { FR: 12 }; a missing country follows the open demand
    volumes?: Record<string, number>;
}

// Purchase orders store slugs ("pompe-a-chaleur") while the catalog uses labels
// ("Pompe à chaleur"): compare both through the same key.
export const productKey = (s: string) =>
    (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

interface SubIndustry {
    name: string;
    sub?: string[];
    priceMin?: number;
    priceMax?: number;
    imageUrl?: string;
    // Optional overrides; when unset/empty, callers should fall back to the
    // parent Industry's `fields` / `mandatoryFields` — see resolveFields/resolveMandatoryFields.
    fields?: string[];
    mandatoryFields?: string[];
    // Stable numeric "product id" for the generic seller API (POST /lead/api/submit) — see
    // industrySchema.ts on the backend. Auto-assigned; editable from the back-office.
    subIndustryId?: number;
    marketplace?: MarketplaceDisplay;
}

export interface Industry {
    _id: string;
    name: string;
    description?: string;
    forWho?: string;
    pricing: string[];
    fields: string[];
    mandatoryFields: string[];
    steps?: number;
    subIndustries: SubIndustry[];
    createdAt: string;
    updatedAt: string;
}

// Cache for industries
let industriesCache: Industry[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache mapping normalized "industry|||subIndustry" -> admin-provided image URL
const normalize = (str: string) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
};

let subIndustryImageMap: Map<string, string> = new Map();

function buildSubIndustryImageMap(industries: Industry[]) {
    const map = new Map<string, string>();
    for (const industry of industries) {
        for (const sub of industry.subIndustries) {
            if (sub.imageUrl) {
                map.set(`${normalize(industry.name)}|||${normalize(sub.name)}`, sub.imageUrl);
            }
        }
    }
    subIndustryImageMap = map;
}

/**
 * Look up the admin-configured image URL for an industry/sub-industry pair.
 * Backed by whatever the industries cache last held (see fetchIndustries).
 */
export function getSubIndustryImageUrl(industry?: string, subIndustry?: string): string | undefined {
    if (!industry || !subIndustry) return undefined;
    return subIndustryImageMap.get(`${normalize(industry)}|||${normalize(subIndustry)}`);
}

/**
 * Fetch all industries from API
 * Uses caching to reduce API calls
 */
export async function fetchIndustries(forceRefresh = false): Promise<Industry[]> {
    const now = Date.now();

    // Return cached data if available and not expired
    if (!forceRefresh && industriesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
        return industriesCache;
    }

    try {
        const response = await axios.get(`${CONFIG.apiUrl}/industry`);
        industriesCache = response.data;
        cacheTimestamp = now;
        buildSubIndustryImageMap(response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching industries:', error);
        // Return cached data if available, even if expired
        if (industriesCache) {
            return industriesCache;
        }
        throw error;
    }
}

/**
 * Fetch a single industry by ID
 */
export async function fetchIndustryById(id: string): Promise<Industry> {
    const response = await axios.get(`${CONFIG.apiUrl}/industry/${id}`);
    return response.data;
}

/**
 * Fetch industry by name
 */
export async function fetchIndustryByName(name: string): Promise<Industry> {
    const response = await axios.get(`${CONFIG.apiUrl}/industry/name/${encodeURIComponent(name)}`);
    return response.data;
}

/**
 * Effective "available fields" for an industry/sub-industry pair: the
 * sub-industry's own `fields` if it has any, otherwise the parent industry's.
 */
export function resolveFields(industry?: Industry | null, subIndustryName?: string): string[] {
    if (!industry) return [];
    if (subIndustryName) {
        const subIndustry = industry.subIndustries.find(s => s.name === subIndustryName);
        if (subIndustry?.fields && subIndustry.fields.length > 0) {
            return subIndustry.fields;
        }
    }
    return industry.fields || [];
}

/**
 * Effective "mandatory fields" for an industry/sub-industry pair: the
 * sub-industry's own `mandatoryFields` if it has any, otherwise the parent industry's.
 */
// Champ toujours présent et obligatoire dans toutes les iframes (id du FormField), quelle que soit
// la config du template, de l'offre ou de l'industrie. Miroir de ALWAYS_MANDATORY_FIELD côté back.
export const ALWAYS_MANDATORY_FIELD = 'Email';

// Comparaison exacte : seul l'id « Email » existe en FormField. D'anciennes offres listent « email »
// (minuscule), que le formulaire ignore faute de FormField correspondant — il faut quand même ajouter « Email ».
export function withAlwaysMandatoryField(fields: string[]): string[] {
    if (fields.includes(ALWAYS_MANDATORY_FIELD)) return fields;
    // Juste après le téléphone s'il y est, sinon à la fin.
    const phoneIndex = fields.findIndex((f) => f === 'Téléphone' || f.toLowerCase() === 'phone');
    return phoneIndex === -1
        ? [...fields, ALWAYS_MANDATORY_FIELD]
        : [...fields.slice(0, phoneIndex + 1), ALWAYS_MANDATORY_FIELD, ...fields.slice(phoneIndex + 1)];
}

export function resolveMandatoryFields(industry?: Industry | null, subIndustryName?: string): string[] {
    if (!industry) return [];
    if (subIndustryName) {
        const subIndustry = industry.subIndustries.find(s => s.name === subIndustryName);
        if (subIndustry?.mandatoryFields && subIndustry.mandatoryFields.length > 0) {
            return subIndustry.mandatoryFields;
        }
    }
    return industry.mandatoryFields || [];
}

/**
 * Clear the industries cache (useful after creating/updating/deleting)
 */
export function clearIndustriesCache() {
    industriesCache = null;
    cacheTimestamp = null;
    subIndustryImageMap = new Map();
}

/**
 * React hook to use industries in components
 */
export function useIndustries() {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadIndustries = async (forceRefresh = true) => {
        try {
            setLoading(true);
            const data = await fetchIndustries(forceRefresh);
            setIndustries(data);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Failed to load industries');
            console.error('Error loading industries:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadIndustries();
    }, []);

    return {
        industries,
        loading,
        error,
        refresh: () => loadIndustries(true),
    };
}

/**
 * Helper to convert legacy format to new format
 * For backward compatibility during migration
 */
export function convertIndustriesToLegacyFormat(industries: Industry[]): any {
    const industriesPricing: any = {};
    const industriesMap: any = {};

    industries.forEach((industry) => {
        // Build pricing config
        industriesPricing[industry.name] = {
            description: industry.description || '',
            forWho: industry.forWho || '',
            pricing: industry.pricing,
            fields: industry.fields,
            mandatoryFields: industry.mandatoryFields,
            steps: industry.steps,
        };

        // Build industries map
        industriesMap[industry.name] = industry.subIndustries;
    });

    return { industriesPricing, industries: industriesMap };
}
